import React from 'react';
import { Header } from './Header';
import { Columns } from './Columns';
import MissingBlock from 'components/partials/MissingBlock';
import Pagination from '../Pagination';
import { XLCard } from './XLCards';
import { Card } from './Card';
import useWindowSize from 'hooks/useWindowSize';
import { responsiveMap } from 'helpers/constants/responsive';
import { Loading } from 'components/Loading';
import { PAGE_SIZE_30 } from 'helpers/constants/page-size';
import { Header as HeaderType } from 'types/Table';
import { ResponsiveCardsProps } from 'types/CardComponents';
import 'styles/components/table.scss';

interface TableProps {
    tableTitle?: string | false;
    cardFooter?: string | false;
    results?: unknown[];
    className?: string;
    baseTableClass?: string;
    emptyRows?: React.ReactNode;
    canRespond?: boolean;
    respondAt?: string;
    enableTable?: boolean;
    withImg?: boolean;
    rowClicked?: boolean | ((row: unknown) => void);
    hasPagination?: boolean;
    paginationPerPage?: number;
    totalRecords?: number;
    currentPage?: number;
    handleRedoSearch?: (search: { 
        search_obj?: unknown;
        currentPage?: number;
        sortColumn?: { column_map: string; sort_asc: boolean };
        sortAsc?: boolean;
    }) => void;    
    hasSort?: boolean;
    sortColumn?: { column_map?: string; sort_asc?: boolean };
    sortAsc?: boolean;
    sortableColumns?: number[];
    current_page?: string;
    headers: HeaderType[];
    loading?: boolean;
    loadingTemplate?: React.ReactNode;
    xlCards?: boolean;
    hasActions?: boolean;
    error?: boolean | null;
}

interface SearchChangeParams {
    sortColumn?: HeaderType;
    sortAsc?: boolean;
}

const Table = ({
    tableTitle = false,
    cardFooter = false,
    results = [],
    className = '',
    baseTableClass = 'table table-hover table-stripped',
    emptyRows = <MissingBlock/>,
    canRespond = true,
    enableTable = true,
    withImg = false,
    respondAt = 'lg',
    rowClicked = false,
    hasPagination = true,
    paginationPerPage = PAGE_SIZE_30,
    totalRecords = -1,
    currentPage = 1,
    handleRedoSearch = () => alert('missing the handleRedoSearch function'),
    hasSort = false,
    sortColumn = undefined,
    sortAsc = false,
    sortableColumns = [],
    current_page = '1',
    headers,
    loading = false,
    loadingTemplate = <Loading show={true}/>,
    xlCards = true,
    hasActions,
    error,
}: TableProps) => {
    const { width } = useWindowSize();

    const tableClass = `${baseTableClass} ${className}`;

    const Title = () => tableTitle && <div className="table-title">{tableTitle}</div>;

    const Footer = () => cardFooter && <div className="card-footer">{cardFooter}</div>;

    const ResponsiveCards = ({ 
        results, 
        hasActions, 
        headers, 
        withImg, 
        rowClicked, 
        className,
    }: ResponsiveCardsProps) =>
        xlCards ? (
            <XLCard 
                results={results}
                hasActions={hasActions}
                headers={headers}
                withImg={withImg}
                rowClicked={rowClicked}
                className={className}
            />
        ) : (
            <Card 
                results={results}
                hasActions={hasActions}
                headers={headers}
                withImg={withImg}
                rowClicked={rowClicked}
                className={className}
            />
        );

    const triggerSearchChangePage = (page_num: number) => {
        handleRedoSearch({ currentPage: page_num });
    };

    interface SearchChangeParams {
    sortColumn?: HeaderType;
    sortAsc?: boolean;
}

    const triggerSearchChange = (changed: SearchChangeParams = {}, newPage: number = 1) => {
        const columnToUse = changed.sortColumn;

        const apiMap = columnToUse?.apiMap || false;
        const column_map = columnToUse?.column_map || sortColumn?.column_map;   

        const search = {
            sortColumn: { 
                column_map: (apiMap || column_map) as string, 
                sort_asc: changed.sortAsc as boolean,
            },
            sortAsc: changed.sortAsc as boolean,
            currentPage: newPage || Number(current_page),
        };  

        handleRedoSearch(search);
    };

    if (!error && !loading && results.length) {
        const responsiveBreakpoint =
            Object.keys(responsiveMap)
                .find(key => responsiveMap[key] === respondAt) as unknown as number - 1 ||
            Number(respondAt) - 1;

        return (
            <div className="table-container is-table">
                <Title/>
                {(canRespond && (width <= responsiveBreakpoint)) || !enableTable ? (
                    <ResponsiveCards
                        results={results}
                        hasActions={hasActions}
                        headers={headers}
                        withImg={withImg}
                    />
                ) : (
                    <table className={tableClass}>
                        <Header
                            {...{
                                results,
                                triggerSearchChange,
                                sortColumn: sortColumn?.column_map,
                                sortableColumns,
                                headers,
                                hasSort,
                                sortAsc: sortColumn?.sort_asc,
                            }}
                        />
                        <Columns headers={headers} results={results} rowClicked={rowClicked}/>
                    </table>
                )}
                {hasPagination && (
                    <Pagination
                        paginationPerPage={paginationPerPage}
                        dataCount={totalRecords}
                        currentPage={currentPage}
                        onSetPage={triggerSearchChangePage}
                    />
                )}
                <Footer/>
            </div>
        );
    }

    return (
        <div className="mt-3 d-flex justify-content-center">
            {loading ? loadingTemplate : error ? null : emptyRows}
        </div>
    );
};

export default Table;
